import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { Dropdown, Badge } from 'react-bootstrap';
import { Employee } from 'data/users';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { DELETE_EMPLOYEE } from 'services/api/employee';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

const handleDelete = async (employeeId: string) => {
  try {
    await DELETE_EMPLOYEE(employeeId);
  } catch (error) {
    console.error('Error deleting customer:', error);
  }
};

export const employeesTableColumns = (
  canEdit = false,
  canDelete = false
): ColumnDef<Employee>[] => [
  {
    accessorKey: 'firstName',
    header: 'Member',
    cell: ({ row: { original } }) => {
      const { firstName, lastName, verified } = original;
      return (
        <div className="d-flex align-items-center">
          <div>
            <p className="mb-0 text-1100 fw-semi-bold">
              {firstName} {lastName}
            </p>
            <Badge
              bg={verified ? 'success' : 'warning'}
              className="mt-1"
              style={{ fontSize: '11px' }}
            >
              {verified ? 'Verified' : 'Unverified'}
            </Badge>
          </div>
        </div>
      );
    },
    meta: {
      headerProps: {
        style: { width: '15%', minWidth: '200px' },
        className: 'px-1'
      },
      cellProps: { className: 'align-middle white-space-nowrap py-2' }
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row: { original } }) => {
      const { email } = original;
      return (
        <Link to={`mailto:${email}`} className="fw-semi-bold">
          {email}
        </Link>
      );
    },
    meta: {
      headerProps: {
        style: { width: '15%', minWidth: '200px' }
      },
      cellProps: { className: 'white-space-nowrap' }
    }
  },
  {
    accessorKey: 'phoneNumber',
    header: 'Mobile Number',
    cell: ({ row: { original } }) => {
      const { phoneNumber } = original;
      return (
        <Link to={`tel:${phoneNumber}`} className="fw-bold text-1100">
          {phoneNumber}
        </Link>
      );
    },
    meta: {
      headerProps: {
        style: { width: '20%', minWidth: '200px' },
        className: 'pe-3'
      }
    }
  },
  {
    accessorKey: 'roles',
    header: 'User Type',
    cell: ({ row: { original } }) => {
      if (original?.isAdmin) {
        return <div className="fw-bold text-1100">Admin</div>;
      }

      if (original?.roles && original?.roles?.length > 0) {
        return (
          <div className="fw-bold text-1100">
            {original?.roles[0]?.name || 'N/A'}
          </div>
        );
      }

      return <div className="fw-bold text-1100">No Role</div>;
    },
    meta: {
      headerProps: {
        style: { width: '21%', minWidth: '200px' },
        className: 'text-end'
      },
      cellProps: {
        className: 'text-end text-700'
      }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Joined',
    cell: ({ row: { original } }) => {
      const { createdAt } = original;
      const myDate = new Date(createdAt as Date);
      return (
        <div className="fw-bold text-1100">
          <HumanReadableTime date={myDate} />
        </div>
      );
    },
    meta: {
      headerProps: {
        style: { width: '19%', minWidth: '200px' },
        className: 'text-end pe-0'
      },
      cellProps: {
        className: 'text-700 text-end'
      }
    }
  },
  {
    id: 'leadDropdown',
    cell: ({ row: { original } }) => {
      const employeeId = (original as any).id || original._id;
      const navigate = useNavigate();
      const queries = useQueryClient();

      const handleEdit = () => {
        navigate(`/home/apps/employee/edit-employee/${employeeId}`);
      };
      const handleDeleteClick = async (employeeId: string) => {
        try {
          await handleDelete(employeeId);
          toast.success('Employee deleted successfully');
          queries.invalidateQueries('get_employee');
          queries.invalidateQueries('getEmployees');
          navigate(`/home/apps/employee/`);
        } catch (error: any) {
          console.error('Error handling delete:', error);
          toast.error(error.response.data.message || 'Error deleting employee');
        }
      };

      if (!canEdit && !canDelete) {
        return null;
      }

      return (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <>
              {canEdit && (
                <Dropdown.Item eventKey="2" onClick={handleEdit}>
                  Edit
                </Dropdown.Item>
              )}
              {canDelete && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    eventKey="4"
                    className="text-danger"
                    onClick={() => handleDeleteClick(employeeId)}
                  >
                    Delete
                  </Dropdown.Item>
                </>
              )}
            </>
          </RevealDropdown>
        </RevealDropdownTrigger>
      );
    },
    meta: {
      cellProps: {
        className: 'text-end pe-0 ps-4'
      }
    }
  }
];
